import { BasicButton, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useProfile from "@/hooks/useProfile";
import { CASE_MEMBER_TYPE, LETTER_TYPE_VALUE } from "@/libraries/constant";
import { parseDate, readableHumanDate } from "@/libraries/dayjs-relative";
import { getCaseLetter, getCaseQueryOptions } from "@/queries";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import Divider from "@mui/material/Divider";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import { useMemo } from "react";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/letter/$letterId/",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { profile } = useProfile();
	const letterId = Route.useParams({
		select: (s) => s.letterId,
	});

	const resCaseLetter = useSuspenseQuery(getCaseLetter(letterId));

	const caseLetter = useMemo(() => {
		return {
			...resCaseLetter.data.case_letter,
			date: resCaseLetter.data.case_letter.date
				? dayjs(resCaseLetter.data.case_letter.date).format("DD MMMM YYYY")
				: "-",
			dateDiff: resCaseLetter.data.case_letter.date
				? readableHumanDate(resCaseLetter.data.case_letter.date, "YYYY-MM-DD")
				: "-",
			time: resCaseLetter.data.case_letter.time ?? "-",
			diagnosis: !isEmpty(resCaseLetter.data.case_letter.diagnosis)
				? resCaseLetter.data.case_letter.diagnosis
				: "-",
			medication: !isEmpty(resCaseLetter.data.case_letter.medication)
				? resCaseLetter.data.case_letter.medication
				: "-",
			allergies: !isEmpty(resCaseLetter.data.case_letter.allergies)
				? resCaseLetter.data.case_letter.allergies
				: "-",
			summary: !isEmpty(resCaseLetter.data.case_letter.summary)
				? resCaseLetter.data.case_letter.summary
				: "-",
			plan: !isEmpty(resCaseLetter.data.case_letter.plan)
				? resCaseLetter.data.case_letter.plan
				: "-",
			submittedAt: parseDate(resCaseLetter.data.case_letter.created_at).format(
				"DD MMMM YYYY, HH:mm A",
			),
			submittedDiff: readableHumanDate(
				parseDate(resCaseLetter.data.case_letter.created_at),
			),
			submittedBy: resCaseLetter.data.case_letter.created_by.fullname,
			updatedAt: resCaseLetter.data.case_letter.updated_by
				? parseDate(resCaseLetter.data.case_letter.updated_at).format(
						"DD MMMM YYYY, HH:mm A",
					)
				: "-",
			updatedDiff: resCaseLetter.data.case_letter.updated_by
				? readableHumanDate(
						parseDate(resCaseLetter.data.case_letter.updated_at),
					)
				: null,
			updatedBy: resCaseLetter.data.case_letter.updated_by
				? resCaseLetter.data.case_letter.updated_by.fullname
				: "-",
		};
	}, [resCaseLetter.data]);

	const { data: caseItem } = useSuspenseQuery(
		getCaseQueryOptions(caseLetter.case_id),
	);

	const currentUserRole = useMemo(() => {
		if (!profile) return "external" as const;
		if (caseItem.user.id === profile.id) return "owner";
		if (
			caseItem.member_type === CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER ||
			caseItem.external_members.some((member) => member.id === profile.id)
		)
			return "external" as const;
		if (caseItem.internal_members.some((member) => member.id === profile.id))
			return "member" as const;
		return "member";
	}, [profile, caseItem]);

	return (
		<>
			<BackAppBar title={LETTER_TYPE_VALUE[caseLetter.letter_type]} />
			<div className="flex-1 bg-gray-light flex flex-col gap-2 overflow-y-auto">
				<div className="container bg-white flex flex-col gap-2 py-4">
					<Typography fontWeight={900} fontSize={14}>
						Letter Details
					</Typography>
					<div className="grid grid-cols-2 gap-1">
						<div className="flex flex-col gap-1">
							<Typography fontWeight={400} fontSize={14}>
								Date
							</Typography>
							<Typography fontWeight={700} fontSize={14}>
								{caseLetter.date ?? "-"}
							</Typography>
							{resCaseLetter.data.case_letter.date ? (
								<Typography fontWeight={400} fontSize={14}>
									({caseLetter.dateDiff})
								</Typography>
							) : null}
						</div>
						<div className="flex flex-col gap-1">
							<Typography fontWeight={400} fontSize={14}>
								Time
							</Typography>
							<Typography fontWeight={700} fontSize={14}>
								{caseLetter.time ?? "-"}
							</Typography>
						</div>
					</div>
					<Divider />
					<div className="flex flex-col gap-1">
						<Typography fontWeight={400} fontSize={14}>
							Diagnosis
						</Typography>
						<Typography fontWeight={700} fontSize={14}>
							{caseLetter.diagnosis ?? "-"}
						</Typography>
					</div>
					<Divider />
					<div className="flex flex-col gap-1">
						<Typography fontWeight={400} fontSize={14}>
							Medication
						</Typography>
						<Typography fontWeight={700} fontSize={14}>
							{caseLetter.medication ?? "-"}
						</Typography>
					</div>
					<Divider />
					<div className="flex flex-col gap-1">
						<Typography fontWeight={400} fontSize={14}>
							Allergies
						</Typography>
						<Typography fontWeight={700} fontSize={14}>
							{caseLetter.allergies ?? "-"}
						</Typography>
					</div>
					<Divider />
					<div className="flex flex-col gap-1">
						<Typography fontWeight={400} fontSize={14}>
							Case Summary / Message
						</Typography>
						<Typography fontWeight={700} fontSize={14}>
							{caseLetter.summary ?? "-"}
						</Typography>
					</div>
					<Divider />
					<div className="flex flex-col gap-1">
						<Typography fontWeight={400} fontSize={14}>
							Plan
						</Typography>
						<Typography fontWeight={700} fontSize={14}>
							{caseLetter.plan ?? "-"}
						</Typography>
					</div>
				</div>
				<div className="container bg-white flex flex-col gap-2 py-4">
					<Typography fontWeight={900} fontSize={14}>
						Submission Details
					</Typography>
					<div className="grid grid-cols-2 gap-1">
						<div className="flex flex-col gap-1">
							<Typography fontWeight={400} fontSize={14}>
								Submitted at
							</Typography>
							<Typography fontWeight={700} fontSize={14}>
								{caseLetter.submittedAt}
							</Typography>
							<Typography fontWeight={400} fontSize={14}>
								({caseLetter.submittedDiff})
							</Typography>
						</div>
						<div className="flex flex-col gap-1">
							<Typography fontWeight={400} fontSize={14}>
								Submitted by
							</Typography>
							<Typography fontWeight={700} fontSize={14}>
								{caseLetter.submittedBy}
							</Typography>
						</div>
					</div>
					<Divider />
					<div className="grid grid-cols-2 gap-1">
						<div className="flex flex-col gap-1">
							<Typography fontWeight={400} fontSize={14}>
								Submitted at
							</Typography>
							<Typography fontWeight={700} fontSize={14}>
								{caseLetter.updatedAt}
							</Typography>
							{caseLetter.updatedDiff ? (
								<Typography fontWeight={400} fontSize={14}>
									({caseLetter.updatedDiff})
								</Typography>
							) : null}
						</div>
						<div className="flex flex-col gap-1">
							<Typography fontWeight={400} fontSize={14}>
								Updated by
							</Typography>
							<Typography fontWeight={700} fontSize={14}>
								{caseLetter.updatedBy}
							</Typography>
						</div>
					</div>
				</div>
			</div>
			{caseItem.status === "active" && currentUserRole !== "external" ? (
				<div className="container grid grid-cols-2 gap-2 bg-white py-4 shadow">
					<Link
						to="/dashboard/case/view/$id/letter/$letterId/export"
						params={{ id: caseLetter.case_id, letterId: caseLetter.id }}
					>
						<BasicButton
							variant="outlined"
							color="secondary"
							className="!h-12 text-sm font-bold !shadow-none"
							fullWidth
							startIcon={<FileUploadRoundedIcon />}
						>
							Send as email
						</BasicButton>
					</Link>
					<Link
						to="/dashboard/case/view/$id/letter/$letterId/update"
						params={{ id: caseLetter.case_id, letterId: caseLetter.id }}
					>
						<BasicButton
							variant="contained"
							color="secondary"
							className="!h-12 text-sm font-bold !shadow-none"
							fullWidth
							startIcon={<EditRoundedIcon />}
						>
							Update
						</BasicButton>
					</Link>
				</div>
			) : null}
		</>
	);
}
